import { faClose, faSave } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Drawer, Form, Input, InputNumber, message, Select, Space } from "antd";
import { useEffect, useState } from "react";
import CustomDateTimePicker from '../common/CustomDateTimePicker';
import { CountryCodeSelect, CurrencyCodeSelect, RoleSelect, TimezoneSelect } from '../helpers/common';
import ContainerService from "../services/Container/ContainerService";
import OrgService from "../services/Org/OrgService";
import ShipmentService from "../services/Shipment/ShipmentService";
import StockService from "../services/Stock/StockService";
import UserService from "../services/User/UserService";

const AddEditFormComponent = ({
  open,
  onClose,
  onSubmit,
  initialValues,
  type,
  onSuccess,
  formType,
  roles,
  userRole,
  orgID
}) => {
  const [clientOrgs, setClientOrgs] = useState([]);
  const [supplierOrgs, setSupplierOrgs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [forwarders, setForwarders] = useState([]);
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const supplierRoleId = roles?.find((role) => role.name === "Supplier")?.id;

  useEffect(() => {
    switch (formType) {
      case "supplier":
        break;
      case "user":
        fetchClientOrgs();
        fetchSupplierOrgs();
        break;
      case "container":
        fetchSuppliers();
        fetchForwarders();
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    if (open) {
      if (initialValues) {
        switch (formType) {
          case "supplier":
            form.setFieldsValue({ ...initialValues, ...initialValues.address });
            break;
          default:
            form.setFieldsValue(initialValues);
            break;
        }
      } else {
        form.resetFields();
        let defaultValues = form.getFieldsValue();
        switch (formType) {
          case "supplier":
            defaultValues.countryCd = "AU";
            defaultValues.currencyCd = "USD";
            break;
          case "container":
            defaultValues.containerType = "40HC";
            break;
          default:
            break;
        }
        form.setFieldsValue(defaultValues);
      }
    }
  }, [open, initialValues]);

  const fetchClientOrgs = () => {
    setIsLoading(true);
    OrgService.getAllClientOrgsDropDown()
      .then((response) => {
        if (response.status === 200) {
          setClientOrgs(response.data);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching client orgs:", error);
      });
  };

  const fetchSupplierOrgs = () => {
    setIsLoading(true);
    OrgService.getAllSupplierOrgsDropDown()
      .then((response) => {
        if (response.status === 200) {
          setSupplierOrgs(response.data);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching supplier:", error);
      });
  };

  const fetchSuppliers = () => {
    setIsLoading(true);
    OrgService.getSupplierDropDown()
      .then((response) => {
        if (response.status === 200) {
          const sortedSuppliers = response.data.sort((a, b) =>
            a.supplierShortCode.localeCompare(b.supplierShortCode)
          );
          setSuppliers(sortedSuppliers);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching supplier:", error);
      });
  };

  const fetchForwarders = () => {
    setIsLoading(true);
    ContainerService.getForwarders()
      .then((response) => {
        if (response.status === 200) {
          setForwarders(response.data);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching forwarders:", error);
      });
  };

  const handleSubmit = async (values) => {
    try {
      setIsLoading(true);
      let response;
      switch (formType) {
        case "supplier":
          if (initialValues) {
            response = await OrgService.updateOrgSupplier({
              ...values,
              id: initialValues.id,
            });
          } else {
            response = await OrgService.createOrgSupplier(values);
          }
          break;
        case "user":
          if (initialValues) {
            response = await UserService.updateUser({
              ...values,
              id: initialValues.id,
            });
          } else {
            response = await UserService.createUser({
              ...values,
              orgID: orgID
            });
          }
          break;
        case "stock":
          if (initialValues) {
            response = await StockService.updateStock({
              ...values,
              id: initialValues.id,
            });
          } else {
            response = await StockService.addStock(values);
          }
          break;
        case "shipment":
          const formattedValues = {
            ...values,
            purchaseOrderID: initialValues.purchaseOrderID,
          };
          // if (initialValues) {
          //   response = await ShipmentService.updateShipment({ ...formattedValues, id: initialValues.id });
          // } else {
          response = await ShipmentService.createShipment(formattedValues);
          initialValues = null;
          // }
          break;
        case "container":
          // if (initialValues) {
          //   response = await ContainerService.update({
          //     ...values,
          //     id: initialValues.id,
          //   });
          // } else {
          response = await ContainerService.saveContainer(values);
          //  }
          break;

        default:
          break;
      }

      if (response?.status === 200) {
        message.success(
          initialValues
            ? `${formType.charAt(0).toUpperCase() + formType.slice(1)
            } updated successfully!`
            : `${formType.charAt(0).toUpperCase() + formType.slice(1)
            } added successfully!`
        );
        form.resetFields();
        onSubmit && onSubmit(values);
        onSuccess();
        handleClose();
      } else {
        message.error(
          response?.data?.message || "An error occurred. Please try again."
        );
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message || "An unexpected error occurred."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const renderFormFields = () => {
    switch (formType) {
      case "supplier":
        return (
          <>
            <Form.Item
              name="name"
              label="Company"
              rules={[{ required: true, message: "Please Enter Company Name" }]}
            >
              <Input placeholder="Enter Company Name" />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Please Enter Email" },
                { type: "email", message: "Invalid Email" },
              ]}
            >
              <Input placeholder="Enter Email" />
            </Form.Item>

            <Form.Item label="Phone Number" required>
              <Space.Compact className="w-full">
                <Form.Item
                  name="phoneCode"
                  noStyle
                  rules={[{ message: "Enter Code", min: 1, max: 5 }]}
                >
                  <Input
                    addonBefore={"+"}
                    placeholder="Enter"
                    className="w-1/3"
                  />
                </Form.Item>

                <Form.Item
                  name="phoneNumber"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: "Enter Phone Number",
                      min: 1,
                      max: 15,
                    },
                  ]}
                >
                  <Input placeholder="Phone Number" className="w-2/3" />
                </Form.Item>
              </Space.Compact>
            </Form.Item>

            <Form.Item
              name="addressLine1"
              label="Address Line 1"
              rules={[
                {
                  required: true,
                  message: "Please Enter Suite / Floor / Building etc.",
                },
              ]}
            >
              <Input.TextArea placeholder="Enter Suite / Floor / Building etc." />
            </Form.Item>

            <Form.Item
              name="addressLine2"
              label="Address Line 2"
              rules={[
                {
                  required: false,
                  message: "Please Enter Street No. / Street Name",
                },
              ]}
            >
              <Input.TextArea placeholder="Enter Street No. / Street Name" />
            </Form.Item>

            <Form.Item
              name="zipCode"
              label="ZIP Code / Postcode"
              rules={[
                { required: true, message: "Please Enter ZIP Code / Postcode" },
              ]}
            >
              <Input placeholder="Enter Zip Code / Postcode" />
            </Form.Item>

            <Form.Item
              name="city"
              label="City"
              rules={[{ required: true, message: "Please Enter City" }]}
            >
              <Input placeholder="Enter City" />
            </Form.Item>

            <Form.Item
              name="state"
              label="State / District"
              rules={[
                { required: true, message: "Please Enter State / District" },
              ]}
            >
              <Input placeholder="Enter State / District" />
            </Form.Item>

            <Form.Item
              name="countryCd"
              label="Country"
              rules={[{ required: true, message: "Please Select Country" }]}
            >
              <CountryCodeSelect placeholder="Select Country" />
            </Form.Item>

            <Form.Item
              name="clientOrgReferenceNumber"
              label="Supplier Number (If any)"
            >
              <Input placeholder="Enter Supplier Number" />
            </Form.Item>

            <Form.Item name="specialNote" label="Special Notes">
              <Input.TextArea placeholder="Enter Special Notes" />
            </Form.Item>

            <Form.Item
              name="currencyCd"
              label="Currency"
              rules={[{ required: true, message: "Please Select Currency" }]}
            >
              <CurrencyCodeSelect placeholder="Select Currency" />
            </Form.Item>

            <Form.Item
              name="supplierShortCode"
              label="Short Code"
              rules={[
                { required: true, message: "Please Enter Short Code" },
                { max: 10, message: "Short Code must be at most 10 characters" }
              ]}
            >
              <Input placeholder="Enter Short Code" maxLength={10} />
            </Form.Item>

            <Form.Item
              name="defaultPOL"
              label="Port of Loading"
              rules={[
                { required: true, message: "Please Enter Port of Loading" },
              ]}
            >
              <Input placeholder="Enter Port of Loading" />
            </Form.Item>
          </>
        );
      case "user":
        return (
          <>
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[{ required: true, message: "Please Enter First Name" }]}
            >
              <Input placeholder="Enter First Name" />
            </Form.Item>

            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[{ required: true, message: "Please Enter Last Name" }]}
            >
              <Input placeholder="Enter Last Name" />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true, message: "Please Enter Email" }]}
            >
              <Input placeholder="Enter Email" disabled={type === "edit"} />
            </Form.Item>

            <Form.Item
              name="role"
              label="Role"
              rules={[{ required: true, message: "Please Select Role" }]}
            >
              <RoleSelect placeholder="Select Role" disabled={type === "edit" && (userRole !== "ContiAdmin" || initialValues?.role === supplierRoleId)} form={form} />
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) => {
                const selectedRoleId = getFieldValue("role") || null;
                const hasUserRole = selectedRoleId && roles?.some((r) => selectedRoleId === r.id);
                return (type === "edit" && userRole === "ContiAdmin" && hasUserRole) ? (
                  <Form.Item
                    name="orgIDs"
                    label="Org(s)"
                    rules={[{ required: true, message: "Please Select Org(s)" }]}
                  >
                    <Select
                      mode="multiple"
                      showSearch
                      optionFilterProp="label"
                      filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
                      onChange={(selectedValues) => form.setFieldsValue({ orgIDs: clientOrgs.filter((c) => selectedValues.includes(c.id)).map((c) => c.id) })}
                      disabled
                    >
                      {clientOrgs.map((c) => (
                        <Select.Option key={c.id} value={c.id} label={c.name}>
                          {c.supplierShortCode}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
              {({ getFieldValue }) => {
                const selectedRoleId = getFieldValue("role") || null;
                const hasSupplierRole = selectedRoleId && roles?.some((r) => selectedRoleId === r.id && r.name === "Supplier");
                return hasSupplierRole ? (
                  <Form.Item
                    name="supplierID"
                    label="Supplier"
                    rules={[{ required: true, message: "Please Select Supplier" }]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
                      onChange={(selectedValue) => form.setFieldsValue({ supplierID: supplierOrgs.find((s) => selectedValue === s.id)?.id || null })}
                      disabled={type === "edit"}
                    >
                      {supplierOrgs.map((s) => (
                        <Select.Option key={s.id} value={s.id} label={s.name}>
                          {s.supplierShortCode}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>

            <Form.Item
              name="timezone"
              label="Timezone"
              rules={[{ required: true, message: "Please Select Timezone" }]}
            >
              <TimezoneSelect placeholder="Select Timezone" />
            </Form.Item>
          </>
        );
      case "stock":
        return (
          <>
            <Form.Item
              name="sku"
              label="SKU"
              rules={[{ required: true, message: "Please Enter SKU" }]}
            >
              <Input type="text" placeholder="Enter SKU" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: "Please Enter Description" }]}
            >
              <Input type="text" placeholder="Enter Description" />
            </Form.Item>

            <Form.Item
              name="qoh"
              label="QOH"
              rules={[{ required: true, message: "Please Enter QOH" }]}
            >
              <InputNumber
                controls={false}
                placeholder="Enter QOH"
                precision={4}
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              name="palletQty"
              label="Pallet Qty"
              rules={[{ required: true, message: "Please Enter Pallet Qty" }]}
            >
              <InputNumber
                controls={false}
                placeholder="Enter Pallet Qty"
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              name="packSize"
              label="Pack Size"
              rules={[{ required: true, message: "Please Enter Pack Size" }]}
            >
              <InputNumber
                controls={false}
                placeholder="Enter Pack Size"
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              name="dimensionHeightCm"
              label="Height(cm)"
              rules={[{ required: true, message: "Please Enter Height(cm)" }]}
            >
              <InputNumber
                controls={false}
                placeholder="Enter Height(cm)"
                precision={4}
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              name="dimensionWidthCm"
              label="Width(cm)"
              rules={[{ required: true, message: "Please Enter Width(cm)" }]}
            >
              <InputNumber
                controls={false}
                placeholder="Enter Width(cm)"
                precision={4}
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              name="dimensionLengthCm"
              label="Length(cm)"
              rules={[{ required: true, message: "Please Enter Length(cm)" }]}
            >
              <InputNumber
                controls={false}
                placeholder="Enter Length(cm)"
                precision={4}
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              name="dimensionWeightKg"
              label="Weight(kg)"
              rules={[{ required: true, message: "Please Enter Weight(kg)" }]}
            >
              <InputNumber
                controls={false}
                placeholder="Enter Weight(kg)"
                precision={4}
                className="w-full"
              />
            </Form.Item>
          </>
        );
      case "shipment":
        return (
          <>
            <Form.Item name="purchaseOrderID" noStyle>
              <Input type="hidden" />
            </Form.Item>

            {/* <Form.Item
              name="cargoReadyDate"
              label="Cargo Ready Date"
              rules={[
                { required: true, message: "Please Select Cargo Ready Date" },
              ]}
            >
              <DatePicker
                className="w-full"
                placeholder="Select Cargo Ready Date"
                format="DD MMM YYYY"
              />
            </Form.Item> */}

            <Form.Item
              name="forwarder"
              label="Forwarder"
              rules={[{ required: true, message: "Please Enter Forwarder" }]}
            >
              <Input placeholder="Enter Forwarder" />
            </Form.Item>

            <Form.Item
              name="portOfLoading"
              label="Port of Loading"
              rules={[
                { required: true, message: "Please Enter Port of Loading" },
              ]}
            >
              <Input placeholder="Enter Port of Loading" />
            </Form.Item>

            <Form.Item
              name="portOfDischarge"
              label="Port of Discharge"
              rules={[
                { required: true, message: "Please Enter Port of Discharge" },
              ]}
            >
              <Input placeholder="Enter Port of Discharge" />
            </Form.Item>
          </>
        );
      case "container":
        return (
          <>
            <Form.Item
              label="Container Number"
              name="number"
              rules={[
                {
                  required: true,
                  message: "Please input the container number!",
                },
              ]}
            >
              <Input placeholder="Container Number" />
            </Form.Item>

            <Form.Item
              label="User Reference"
              name="userReference"
              rules={[
                {
                  required: true,
                  message: "Please input the user reference!",
                },
              ]}
            >
              <Input placeholder="User Reference" />
            </Form.Item>
            <Form.Item
              name="supplierID"
              label="Supplier"
              rules={[{ required: true, message: "Please Select Supplier" }]}
            >
              <Select
                placeholder="Select Supplier"
                onChange={(value) => {
                  form.setFieldsValue({
                    portOfLoading: suppliers?.find((c) => c.id === value)?.defaultPOL || "",
                  });
                }}
              >
                {suppliers?.map((c) => (
                  <Select.Option key={c.id} value={c.id}>
                    {c.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="containerType"
              label="Type"
              rules={[{ required: true, message: "Please Select Container Type" }]}
            >
              <Select
                placeholder="Select Container Type"
              >
                {["20GP", "40GP", "40HC", "45HC", "LCL"].map((type, index) => (
                  <Select.Option key={index} value={type}>
                    {type}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Forwarder"
              name="forwarder"
              rules={[
                { required: true, message: "Please Select Forwarder" },
              ]}
            >
              <Select
                placeholder="Select Forwarder"
              >
                {forwarders?.map((f) => (
                  <Select.Option key={f.id} value={f.id}>
                    {f.companyName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Port of Loading"
              name="portOfLoading"
              rules={[
                {
                  required: false,
                  message: "Please input the port of Loading!",
                },
              ]}
            >
              <Input placeholder="Port of Loading" />
            </Form.Item>
            <Form.Item
              label="Contents"
              name="contents"
              rules={[
                { required: true, message: "Please input the contents!" },
              ]}
            >
              <Input placeholder="Contents" />
            </Form.Item>
            <Form.Item
              label="Boxes"
              name="boxes"
              rules={[{ required: false, message: "Please input the boxes!" }]}
            >
              <Input placeholder="boxes" />
            </Form.Item>

            <Form.Item
              label="Pallets"
              name="pallets"
              rules={[
                { required: false, message: "Please input the pallets!" },
              ]}
            >
              <Input placeholder="Pallets" />
            </Form.Item>

            <Form.Item
              label="Short Description"
              name="shortDescription"
              rules={[
                {
                  required: false,
                  message: "Please input the short description!",
                },
              ]}
            >
              <Input placeholder="Short Description" />
            </Form.Item>

            <Form.Item
              label="Boxes Unload Man Hours"
              name="boxesUnloadManHours"
              rules={[
                {
                  required: false,
                  InputNumber: true,
                  message: "Please input the boxes Unload Man Hours!",
                },
              ]}
            >
              <Input placeholder="Boxes Unload Man Hours" />
            </Form.Item>

            <Form.Item
              label="Pallets Unload Man Hours"
              name="palletsUnloadManHours"
              rules={[
                {
                  required: false,
                  InputNumber: true,
                  message: "Please input the Pallet Unload Man Hours!",
                },
              ]}
            >
              <Input placeholder="Pallet Unload Man Hours" />
            </Form.Item>

            <Form.Item
              label="Notes"
              name="notes"
              rules={[{ required: true, message: "Please input the notes!" }]}
            >
              <Input placeholder="Notes" />
            </Form.Item>

            <Form.Item
              name="eta"
              label="ETA"
              rules={[{ required: true, message: "Please Select ETA Date" }]}
            >
              <CustomDateTimePicker
                type="date"
                form={form}
                itemName="eta"
                className="w-full"
                placeholder="Select ETA Date"
              />
            </Form.Item>
            {/* <Form.Item
              name="cargoReadyDate"
              label="Cargo Ready Date"
              rules={[{ required: true, message: "Please Select ETA Date" }]}
            >
              <DatePicker
                className="w-full"
                placeholder="Select Cargo Ready Date"
                format="DD MMM YYYY"
              />
            </Form.Item> */}
          </>
        );
      default:
        return <p>Invalid Form Type</p>;
    }
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      destroyOnClose
      maskClosable={false}
      title={
        <div className="flex items-center justify-between pr-8 capitalize">
          {initialValues && formType !== "shipment"
            ? `Edit ${formType}`
            : `Add New ${formType}`}
        </div>
      }
      width={400}
      className="!bg-white"
    >
      {initialValues || formType === "shipment" ? null : (
        <p className="text-gray-500 mt-0">
          {`Let's add a new ${formType} to your system! Just fill in all the required fields, and you'll be all set.`}
        </p>
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        scrollToFirstError
      >
        {renderFormFields()}

        <div className="flex justify-center">
          <Button className="font-bold" icon={<FontAwesomeIcon icon={faSave} />} htmlType="submit" type="primary" loading={isLoading}>
            Save
          </Button>
          <Button className="font-bold ml-2" icon={<FontAwesomeIcon icon={faClose} />} type="default" onClick={handleClose}>
            Close
          </Button>
        </div>
      </Form>
    </Drawer>
  );
};

export default AddEditFormComponent;